// Admin popup field configuration. Lets mapsly:admin users select which
// Freshsales fields appear in the map pin popup, and in what order.

import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type AutoZoomMode, type PopupPreset, type PopupSize } from '../lib/api';

export default function AdminConfig() {
  const queryClient = useQueryClient();

  const { data: configData, isLoading: configLoading } = useQuery({
    queryKey: ['config'],
    queryFn: () => api.getConfig(),
  });

  const { data: fieldsData, isLoading: fieldsLoading } = useQuery({
    queryKey: ['fields'],
    queryFn: () => api.getFields(),
  });

  const [presets, setPresets] = useState<PopupPreset[]>([]);
  const [editingId, setEditingId] = useState<string>('');
  const [autoZoom, setAutoZoom] = useState<AutoZoomMode>('on-features-change');
  const [clusterMarkers, setClusterMarkers] = useState(true);
  const [popupSize, setPopupSize] = useState<PopupSize>('medium');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (configData?.config.presets) {
      setPresets(configData.config.presets);
      setEditingId((prev) =>
        configData.config.presets.some((p) => p.id === prev)
          ? prev
          : configData.config.presets[0]?.id ?? '',
      );
      setAutoZoom(configData.config.autoZoom ?? 'on-features-change');
      setClusterMarkers(configData.config.clusterMarkers ?? true);
      setPopupSize(configData.config.popupSize ?? 'medium');
    }
  }, [configData]);

  const editing = presets.find((p) => p.id === editingId);
  const selectedFields = editing?.popupFields ?? [];
  const popupTitle = editing?.popupTitle ?? 'name';

  function patchEditing(patch: Partial<PopupPreset>) {
    setPresets((prev) => prev.map((p) => (p.id === editingId ? { ...p, ...patch } : p)));
  }

  function setSelectedFields(next: string[] | ((prev: string[]) => string[])) {
    const value = typeof next === 'function' ? next(selectedFields) : next;
    patchEditing({ popupFields: value });
  }

  function setPopupTitle(title: string) {
    patchEditing({ popupTitle: title });
  }

  function addPreset() {
    const id = `preset-${Date.now()}`;
    const newPreset: PopupPreset = {
      id,
      name: 'New preset',
      popupFields: ['name', 'address'],
      popupTitle: 'name',
    };
    setPresets((prev) => [...prev, newPreset]);
    setEditingId(id);
  }

  function deletePreset(id: string) {
    if (presets.length <= 1) return;
    const remaining = presets.filter((p) => p.id !== id);
    setPresets(remaining);
    if (editingId === id) setEditingId(remaining[0]?.id ?? '');
  }

  function renamePreset(id: string, name: string) {
    setPresets((prev) => prev.map((p) => (p.id === id ? { ...p, name } : p)));
  }

  const mutation = useMutation({
    mutationFn: () => api.updateConfig({ presets, autoZoom, clusterMarkers, popupSize }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const fieldsSyncStatus = useQuery({
    queryKey: ['fieldsSyncStatus'],
    queryFn: () => api.getFieldsSyncStatus(),
    refetchInterval: (query) =>
      query.state.data?.status === 'running' ? 500 : false,
  });

  const isFieldsSyncRunning = fieldsSyncStatus.data?.status === 'running';
  const prevFieldsSyncRunning = useRef(false);
  useEffect(() => {
    if (prevFieldsSyncRunning.current && !isFieldsSyncRunning) {
      queryClient.invalidateQueries({ queryKey: ['fields'] });
    }
    prevFieldsSyncRunning.current = isFieldsSyncRunning;
  }, [isFieldsSyncRunning, queryClient]);

  const startFieldsSync = useMutation({
    mutationFn: () => api.startFieldsSync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fieldsSyncStatus'] });
    },
  });

  const allFields = fieldsData?.fields ?? [];
  const availableFields = allFields.filter((f) => !selectedFields.includes(f));
  const fieldsLastSyncedAt = fieldsData?.lastSyncedAt ?? null;
  const syncProgressPct =
    fieldsSyncStatus.data && fieldsSyncStatus.data.total > 0
      ? Math.min(100, Math.round((fieldsSyncStatus.data.scanned / fieldsSyncStatus.data.total) * 100))
      : 0;

  function addField(field: string) {
    setSelectedFields((prev) => [...prev, field]);
  }

  function removeField(field: string) {
    setSelectedFields((prev) => prev.filter((f) => f !== field));
  }

  function moveField(index: number, direction: -1 | 1) {
    const newFields = [...selectedFields];
    const target = index + direction;
    if (target < 0 || target >= newFields.length) return;
    [newFields[index], newFields[target]] = [newFields[target], newFields[index]];
    setSelectedFields(newFields);
  }

  if (configLoading || fieldsLoading) {
    return <p className="text-sm text-gray-500">Loading configuration...</p>;
  }

  return (
    <div className="space-y-6">
      {/* Presets */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Display-field presets</label>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={editingId}
            onChange={(e) => setEditingId(e.target.value)}
            className="border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none min-w-40"
          >
            {presets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {editing && (
            <input
              type="text"
              value={editing.name}
              onChange={(e) => renamePreset(editing.id, e.target.value)}
              placeholder="Preset name"
              className="border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none flex-1 min-w-40"
            />
          )}
          <button
            onClick={addPreset}
            className="border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-brand-50 hover:border-brand-300"
          >
            + New preset
          </button>
          {editing && presets.length > 1 && (
            <button
              onClick={() => deletePreset(editing.id)}
              className="border border-red-200 bg-white px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
            >
              Delete preset
            </button>
          )}
        </div>
      </div>

      {/* Popup title field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Popup title field</label>
        <select
          value={popupTitle}
          onChange={(e) => setPopupTitle(e.target.value)}
          className="border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none w-full"
        >
          {allFields.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </div>

      {/* Selected fields (popup body) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Popup fields (in order)
        </label>
        {selectedFields.length === 0 && (
          <p className="text-sm text-gray-400 italic">No fields selected. Add fields below.</p>
        )}
        <ul className="space-y-1">
          {selectedFields.map((field, idx) => (
            <li
              key={field}
              className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-3 py-1.5"
            >
              <span className="flex-1 text-sm">{field}</span>
              <button
                onClick={() => moveField(idx, -1)}
                disabled={idx === 0}
                className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30 px-1"
              >
                &uarr;
              </button>
              <button
                onClick={() => moveField(idx, 1)}
                disabled={idx === selectedFields.length - 1}
                className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30 px-1"
              >
                &darr;
              </button>
              <button
                onClick={() => removeField(field)}
                className="text-xs text-red-500 hover:text-red-700 px-1"
              >
                &times;
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Available fields to add */}
      <div>
        <div className="flex items-center justify-between mb-2 gap-3">
          <label className="block text-sm font-medium text-gray-700">Available fields</label>
          <div className="flex items-center gap-2 text-xs">
            {fieldsLastSyncedAt && (
              <span className="text-gray-400">
                last synced {new Date(fieldsLastSyncedAt).toLocaleString()}
              </span>
            )}
            <button
              onClick={() => startFieldsSync.mutate()}
              disabled={isFieldsSyncRunning || startFieldsSync.isPending}
              className="border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-brand-50 hover:border-brand-300 disabled:opacity-50"
            >
              {isFieldsSyncRunning ? 'Syncing…' : 'Sync available fields'}
            </button>
          </div>
        </div>

        {isFieldsSyncRunning && (
          <div className="mb-3">
            <div className="h-1.5 w-full bg-gray-200 overflow-hidden rounded-sm">
              <div
                className="h-full bg-brand-600 transition-all duration-300"
                style={{ width: `${syncProgressPct}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Scanned {fieldsSyncStatus.data?.scanned.toLocaleString() ?? 0}
              {(fieldsSyncStatus.data?.total ?? 0) > 0 &&
                ` of ${fieldsSyncStatus.data?.total.toLocaleString()}`}{' '}
              accounts
            </p>
          </div>
        )}

        {fieldsSyncStatus.data?.status === 'error' && (
          <p className="text-xs text-red-600 mb-2">
            Sync failed: {fieldsSyncStatus.data.error}
          </p>
        )}
        {startFieldsSync.isError && (
          <p className="text-xs text-red-600 mb-2">
            {startFieldsSync.error instanceof Error
              ? startFieldsSync.error.message
              : 'Failed to start sync'}
          </p>
        )}

        {availableFields.length === 0 && !isFieldsSyncRunning && (
          <p className="text-sm text-gray-400 italic">
            {fieldsLastSyncedAt ? 'All fields are selected.' : 'No fields cached yet — click Sync.'}
          </p>
        )}
        <div className="flex flex-wrap gap-1">
          {availableFields.map((field) => (
            <button
              key={field}
              onClick={() => addField(field)}
              className="border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-brand-50 hover:border-brand-300"
            >
              + {field}
            </button>
          ))}
        </div>
      </div>

      {/* Auto-zoom behavior */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Auto-zoom</label>
        <select
          value={autoZoom}
          onChange={(e) => setAutoZoom(e.target.value as AutoZoomMode)}
          className="border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none w-full"
        >
          <option value="on-view-change">Only when a view is selected</option>
          <option value="on-features-change">Whenever the features change (after any sync)</option>
        </select>
        <p className="mt-1 text-xs text-gray-500">
          Controls when the map auto-fits its viewport to the loaded pins.
        </p>
      </div>

      {/* Popup size */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Popup size</label>
        <select
          value={popupSize}
          onChange={(e) => setPopupSize(e.target.value as PopupSize)}
          className="border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none w-full"
        >
          <option value="small">Small</option>
          <option value="medium">Medium</option>
          <option value="large">Large</option>
        </select>
        <p className="mt-1 text-xs text-gray-500">
          Controls the width and text size of the account popup shown when a pin is clicked.
        </p>
      </div>

      {/* Marker clustering */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={clusterMarkers}
            onChange={(e) => setClusterMarkers(e.target.checked)}
            className="h-4 w-4 border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          Cluster markers at low zoom
        </label>
        <p className="mt-1 text-xs text-gray-500">
          Uncheck to render every pin individually — clean look, but expect the map to lag or
          freeze on views with more than a few thousand pins.
        </p>
      </div>

      {/* Save */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="bg-brand-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {mutation.isPending ? 'Saving...' : 'Save configuration'}
        </button>
        {saved && <span className="text-sm text-green-600">Saved</span>}
        {mutation.isError && (
          <span className="text-sm text-red-600">
            {mutation.error instanceof Error ? mutation.error.message : 'Failed to save'}
          </span>
        )}
      </div>
    </div>
  );
}
